// /admin/outreach — Pre-7-July target tracker.
// Bulk import from Instagram HTML/JSON dump, paste, or single add.
// Shortlist → DM → reply → in conversation → applied → onboarded.
// Auto-link button promotes targets when /apply submissions match their handle.
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Mountain, Lock, Upload, ClipboardList, Plus, Search, Filter, RefreshCw,
  Trash2, ExternalLink, MessageCircle, Send, Check, Sparkles, Copy, Star,
  ThumbsDown, ChevronRight, FileText, Wand2, Pencil, X, Save,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const pf = { fontFamily: "'Playfair Display', Georgia, serif" };
const LS_KEY = 'gohi_outreach_pwd_v1';

// Status → label, accent, badge classes
const STATUS_MAP = {
  target_unscreened: { l: 'Unscreened',     c: 'bg-stone-100 text-stone-700 border-stone-200', order: 0 },
  target:            { l: 'Shortlisted',    c: 'bg-amber-100 text-amber-800 border-amber-200', order: 1 },
  dm_sent:           { l: 'DM sent',        c: 'bg-sky-100 text-sky-800 border-sky-200',       order: 2 },
  replied:           { l: 'Replied',        c: 'bg-violet-100 text-violet-800 border-violet-200', order: 3 },
  in_conversation:   { l: 'In conversation',c: 'bg-violet-100 text-violet-800 border-violet-200', order: 4 },
  applied:           { l: 'Applied ⚡',     c: 'bg-emerald-100 text-emerald-900 border-emerald-300', order: 5 },
  onboarded:         { l: 'Onboarded ★',    c: 'bg-emerald-200 text-emerald-900 border-emerald-400 font-semibold', order: 6 },
  declined:          { l: 'Declined',       c: 'bg-stone-100 text-stone-500 border-stone-200',   order: 7 },
  not_a_fit:         { l: 'Not a fit',      c: 'bg-stone-100 text-stone-500 border-stone-200',   order: 8 },
};

// Pre-written DM templates the team can copy + lightly personalise.
// Voice: confident, mission-led. We're building a curated Himalayan platform with
// real tech + marketing weight behind it — hosts are being invited into a
// handpicked editorial cohort, not asked for a favour. No internal launch dates,
// no "small team" framing — what we believe, what we're building, why.
const DM_TEMPLATES = [
  {
    k: 'warm',
    label: 'Warm intro',
    body: (h) => `Hi @${h} 👋

GoHimalayan is a handpicked editorial platform for distinctive stays across the Indian Himalayas. We believe the mountains deserve a platform built around them — not a generic OTA grid — and we're putting the technology, design, and marketing behind it.

Your place stood out. We'd like to feature it as part of our editorial cohort.

Zero commission. Zero fees, ever. Bookings come direct to your WhatsApp — we bring the curation, the audience, and the tech; you keep the relationship with your guests.

Worth a short conversation?`,
  },
  {
    k: 'followup',
    label: 'Follow-up',
    body: (h) => `Hi @${h} — circling back in case the earlier note slipped past.

You're on our shortlist for the next editorial cohort. It's a small, deliberately curated set of Himalayan stays — featured editorially on GoHimalayan, marketed across our channels, with bookings flowing direct to your WhatsApp. No commission, no fee, no platform middle-man.

Happy to walk you through it in 5 minutes whenever works.`,
  },
  {
    k: 'closing',
    label: 'Closing nudge',
    body: (h) => `Hi @${h} — last note from our side on this.

We're locking the next editorial selection soon. If GoHimalayan sounds like the kind of platform you'd want your stay associated with — curated, tech-led, marketing-led, host-owned — all I need is two minutes to walk you through it.

And if it's genuinely not for you, no worries at all — just say the word and we'll quietly take you off the list 🙏`,
  },
];

export default function AdminOutreach() {
  const [pwd, setPwd] = useState(() => { try { return localStorage.getItem(LS_KEY) || ''; } catch (e) { return ''; } });
  const [pwdInput, setPwdInput] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [query, setQuery] = useState('');

  const [showImport, setShowImport] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [importBusy, setImportBusy] = useState(false);
  const [importToast, setImportToast] = useState('');

  const [newRow, setNewRow] = useState({ handle: '', property_name: '', location: '', notes: '' });
  const [addingNew, setAddingNew] = useState(false);

  const [autoLinking, setAutoLinking] = useState(false);
  const [autoLinkResult, setAutoLinkResult] = useState(null);

  const [copiedKey, setCopiedKey] = useState('');
  const [activeDmHandle, setActiveDmHandle] = useState(null);

  // Inline edit state — one row at a time
  const [editingHandle, setEditingHandle] = useState(null);
  const [editForm, setEditForm] = useState({ handle: '', property_name: '', location: '', whatsapp: '', notes: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const fileRef = useRef(null);

  // ─── auth + load ──────────────────────────────────────────────
  useEffect(() => {
    if (pwd) tryLoad(pwd);
    // eslint-disable-next-line
  }, []);

  const tryLoad = async (p) => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API}/api/outreach/list?password=${encodeURIComponent(p)}`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed');
      setData(j);
      setUnlocked(true);
      try { localStorage.setItem(LS_KEY, p); } catch (e) { /* ignore */ }
    } catch (e) {
      setError(e.message);
      setUnlocked(false);
      try { localStorage.removeItem(LS_KEY); } catch (_) { /* ignore */ }
      setPwd('');
    } finally {
      setLoading(false);
    }
  };

  const submitPwd = () => {
    const p = pwdInput.trim();
    if (!p) return;
    setPwd(p);
    tryLoad(p);
  };

  const refresh = () => tryLoad(pwd);

  // ─── derived ──────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.targets.filter((t) => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (sourceFilter !== 'all') {
        if (sourceFilter === 'inbound') {
          if (!['apply_form', 'apply_form_backfill'].includes(t.import_source)) return false;
        } else if (sourceFilter === 'bulk') {
          if (!['paste', 'docx', 'html', 'json'].includes(t.import_source)) return false;
        } else if (t.import_source !== sourceFilter) return false;
      }
      if (!q) return true;
      return (t.handle || '').includes(q)
        || (t.property_name || '').toLowerCase().includes(q)
        || (t.location || '').toLowerCase().includes(q)
        || (t.notes || '').toLowerCase().includes(q);
    });
  }, [data, statusFilter, sourceFilter, query]);

  // ─── helpers ──────────────────────────────────────────────
  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key); setTimeout(() => setCopiedKey(''), 1600);
    }).catch(() => {});
  };

  const setStatus = async (handle, status, extra = {}) => {
    try {
      const r = await fetch(`${API}/api/outreach/update?password=${encodeURIComponent(pwd)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handle, status, ...extra }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      await refresh();
    } catch (e) { setError(e.message); }
  };

  const deleteTarget = async (handle) => {
    if (!confirm(`Delete @${handle}?`)) return;
    try {
      const r = await fetch(`${API}/api/outreach/${encodeURIComponent(handle)}?password=${encodeURIComponent(pwd)}`, { method: 'DELETE' });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      await refresh();
    } catch (e) { setError(e.message); }
  };

  // ─── Inline edit (handle + meta) ─────────────────────────────
  const startEdit = (t) => {
    setEditingHandle(t.handle);
    setEditForm({
      handle: t.handle || '',
      property_name: t.property_name || '',
      location: t.location || '',
      whatsapp: t.whatsapp || '',
      notes: t.notes || '',
    });
    setEditError('');
  };
  const cancelEdit = () => {
    setEditingHandle(null);
    setEditError('');
  };
  const saveEdit = async (originalHandle, currentStatus) => {
    setEditSaving(true); setEditError('');
    try {
      const newHandle = (editForm.handle || '').trim().replace(/^@/, '').toLowerCase();
      // 1. Rename first if the handle changed
      let renameResult = null;
      if (newHandle && newHandle !== originalHandle) {
        const r = await fetch(`${API}/api/outreach/rename?password=${encodeURIComponent(pwd)}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ old_handle: originalHandle, new_handle: newHandle }),
        });
        const rj = await r.json();
        if (!r.ok) throw new Error(rj.detail || 'Could not rename handle');
        renameResult = rj;
      }
      // 2. Update meta on the (possibly-renamed) handle
      const effHandle = newHandle || originalHandle;
      const r2 = await fetch(`${API}/api/outreach/update?password=${encodeURIComponent(pwd)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          handle: effHandle,
          status: currentStatus,  // preserve current status
          property_name: editForm.property_name,
          location: editForm.location,
          whatsapp: editForm.whatsapp,
          notes: editForm.notes,
        }),
      });
      if (!r2.ok) throw new Error((await r2.json()).detail || 'Could not save changes');
      setEditingHandle(null);
      if (renameResult?.stay_synced) {
        setImportToast(`Saved · stay ${renameResult.stay_synced} synced with new Instagram handle.`);
      }
      await refresh();
    } catch (e) {
      setEditError(e.message);
    } finally {
      setEditSaving(false);
    }
  };

  const onFileChosen = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImportBusy(true); setImportToast('');
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await fetch(`${API}/api/outreach/import-file?password=${encodeURIComponent(pwd)}`, { method: 'POST', body: fd });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed');
      setImportToast(`Imported ${j.created} new (${j.found} found) — source: ${j.source}`);
      await refresh();
    } catch (err) {
      setImportToast('Failed: ' + err.message);
    } finally {
      setImportBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const importPaste = async () => {
    if (!pasteText.trim()) return;
    setImportBusy(true); setImportToast('');
    try {
      const r = await fetch(`${API}/api/outreach/import-paste?password=${encodeURIComponent(pwd)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: pasteText }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed');
      setImportToast(`Imported ${j.created} new (${j.found} found)`);
      setPasteText('');
      await refresh();
    } catch (err) {
      setImportToast('Failed: ' + err.message);
    } finally { setImportBusy(false); }
  };

  const addNew = async () => {
    if (!newRow.handle.trim()) return;
    setAddingNew(true);
    try {
      const r = await fetch(`${API}/api/outreach/add?password=${encodeURIComponent(pwd)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRow),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed');
      setNewRow({ handle: '', property_name: '', location: '', notes: '' });
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally { setAddingNew(false); }
  };

  const runAutoLink = async () => {
    setAutoLinking(true); setAutoLinkResult(null);
    try {
      const r = await fetch(`${API}/api/outreach/auto-link-applications?password=${encodeURIComponent(pwd)}`, { method: 'POST' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed');
      setAutoLinkResult(j);
      await refresh();
    } catch (err) { setError(err.message); }
    finally { setAutoLinking(false); }
  };

  // ─── render ──────────────────────────────────────────────
  if (!unlocked) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl border border-stone-200 p-8 max-w-sm w-full shadow-sm" data-testid="outreach-auth">
          <div className="flex items-center gap-2 mb-3">
            <Lock size={18} className="text-amber-700" />
            <h1 className="text-xl font-light italic text-stone-900" style={pf}>Outreach · Admin</h1>
          </div>
          <p className="text-xs text-stone-500 mb-4">Pre-launch host targeting. Founder + teammate only.</p>
          <input
            type="password" value={pwdInput}
            onChange={(e) => setPwdInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submitPwd()}
            placeholder="Admin password"
            className="w-full px-3 py-2 border border-stone-300 rounded text-sm mb-3"
            data-testid="outreach-pwd-input"
            autoFocus
          />
          {error && <p className="text-xs text-rose-700 mb-2">{error}</p>}
          <button onClick={submitPwd} className="w-full bg-stone-900 text-white text-sm py-2 rounded hover:bg-stone-800" data-testid="outreach-unlock-btn">Unlock</button>
        </div>
      </div>
    );
  }

  const progress = data?.progress || {};
  const total = data?.total || 0;
  const onboardedCount = progress.onboarded || 0;
  const onboardedPct = Math.min(100, Math.round((onboardedCount / 100) * 100));
  const goalRemaining = Math.max(0, 100 - onboardedCount);

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-stone-900 text-white px-6 md:px-12 py-8">
        <div className="max-w-7xl mx-auto">
          <Link to="/links" className="text-[11px] uppercase tracking-[0.3em] text-amber-200/70 hover:text-amber-200">← Back to links</Link>
          <div className="flex items-center gap-3 mt-5">
            <Mountain size={26} className="text-amber-300" strokeWidth={1.4} />
            <h1 className="text-3xl md:text-4xl font-light italic" style={pf}>Outreach Tracker</h1>
            <span className="text-xs uppercase tracking-widest text-amber-200/70 ml-2">7 July launch</span>
          </div>

          {/* Burn-down toward 100 onboarded */}
          <div className="mt-6">
            <div className="flex justify-between text-xs text-stone-300 mb-1.5">
              <span><b className="text-white">{onboardedCount}</b> onboarded · <b className="text-white">{goalRemaining}</b> to go</span>
              <span>{onboardedPct}% to 100</span>
            </div>
            <div className="w-full h-2 bg-stone-700 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-400 transition-all duration-500" style={{ width: `${onboardedPct}%` }} data-testid="outreach-progress" />
            </div>
          </div>

          {/* Status counters */}
          <div className="mt-5 flex flex-wrap gap-2 text-[11px]">
            <Stat k="total" v={total} />
            <Stat k="unscreened" v={progress.unscreened || 0} />
            <Stat k="shortlisted" v={progress.target || 0} />
            <Stat k="DM sent" v={progress.dm_sent || 0} />
            <Stat k="in convo" v={progress.in_conversation || 0} />
            <Stat k="applied ⚡" v={progress.applied || 0} highlight />
            <Stat k="onboarded ★" v={progress.onboarded || 0} highlight />
          </div>
        </div>
      </div>

      {/* Tools bar */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search handle, property, location…"
              className="w-full pl-9 pr-3 py-2 border border-stone-300 rounded text-sm" data-testid="outreach-search" />
          </div>
          <Filter size={14} className="text-stone-500" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-2 py-2 border border-stone-300 rounded text-xs" data-testid="outreach-filter-status">
            <option value="all">All statuses</option>
            {Object.entries(STATUS_MAP).sort((a, b) => a[1].order - b[1].order).map(([k, v]) => (
              <option key={k} value={k}>{v.l}</option>
            ))}
          </select>
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="px-2 py-2 border border-stone-300 rounded text-xs" data-testid="outreach-filter-source" title="Filter by how the lead entered the system">
            <option value="all">All sources</option>
            <option value="inbound">Inbound · /apply</option>
            <option value="manual">Manual</option>
            <option value="bulk">Bulk import</option>
            <option value="apply_form">— apply_form (live)</option>
            <option value="apply_form_backfill">— apply_form (backfill)</option>
            <option value="paste">— paste</option>
            <option value="docx">— docx</option>
            <option value="html">— html</option>
            <option value="json">— json</option>
          </select>
          <button onClick={() => setShowImport(!showImport)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-100 hover:bg-amber-200 text-amber-900 rounded font-medium" data-testid="outreach-import-toggle">
            <Upload size={12} /> Bulk import
          </button>
          <button onClick={runAutoLink} disabled={autoLinking} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-emerald-100 hover:bg-emerald-200 text-emerald-900 rounded font-medium disabled:opacity-50" data-testid="outreach-auto-link" title="Match handles to /apply submissions automatically">
            <Wand2 size={12} /> {autoLinking ? 'Linking…' : 'Auto-link /apply'}
          </button>
          <button onClick={refresh} className="ml-auto inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-stone-100 hover:bg-stone-200 rounded">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>

        {/* Bulk import drawer */}
        {showImport && (
          <div className="bg-amber-50/60 border-t border-amber-200 px-6 md:px-12 py-5">
            <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-5">
              {/* File upload */}
              <div className="bg-white rounded-lg border border-stone-200 p-4">
                <h3 className="text-sm font-medium text-stone-900 mb-1 inline-flex items-center gap-1.5"><FileText size={14} className="text-amber-700" /> From Instagram data download</h3>
                <p className="text-[11px] text-stone-500 mb-3 leading-relaxed">
                  Instagram → Settings → Accounts Center → Your information & permissions → Download your information → select "Followers and following" → JSON or HTML format.
                  Then upload the file here.
                </p>
                <input ref={fileRef} type="file" accept=".html,.htm,.json,.txt,.docx" onChange={onFileChosen} className="block w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-stone-900 file:text-white hover:file:bg-stone-800" data-testid="outreach-import-file" />
                <p className="text-[10px] text-stone-400 mt-2">Supports HTML · JSON · Word (.docx) · plain text.</p>
              </div>

              {/* Paste */}
              <div className="bg-white rounded-lg border border-stone-200 p-4">
                <h3 className="text-sm font-medium text-stone-900 mb-1 inline-flex items-center gap-1.5"><ClipboardList size={14} className="text-amber-700" /> Paste handles</h3>
                <p className="text-[11px] text-stone-500 mb-3">One per line, comma-separated, or full Instagram URLs — all accepted.</p>
                <textarea
                  value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={4}
                  placeholder="@bluehouse&#10;instagram.com/forawhile&#10;@chirpingchidiya"
                  className="w-full px-3 py-2 border border-stone-300 rounded text-xs font-mono"
                  data-testid="outreach-paste-text"
                />
                <button onClick={importPaste} disabled={!pasteText.trim() || importBusy} className="mt-2 w-full bg-stone-900 text-white text-xs py-2 rounded hover:bg-stone-800 disabled:opacity-50" data-testid="outreach-paste-submit">
                  {importBusy ? 'Importing…' : `Import (${(pasteText.match(/[A-Za-z0-9._]{2,}/g) || []).length} candidates)`}
                </button>
              </div>
            </div>
            {importToast && <p className="max-w-7xl mx-auto text-[12px] text-emerald-700 mt-3 px-1">{importToast}</p>}
          </div>
        )}

        {/* Auto-link result toast */}
        {autoLinkResult && (
          <div className="bg-emerald-50 border-t border-emerald-200 px-6 md:px-12 py-3 text-[12px] text-emerald-900">
            <span className="font-medium">Auto-link complete:</span> {autoLinkResult.linked_count} target(s) updated.
            {autoLinkResult.linked_count > 0 && (
              <span> · {autoLinkResult.linked.slice(0, 5).map(l => `@${l.handle} → ${l.status}`).join(', ')}{autoLinkResult.linked.length > 5 ? '…' : ''}</span>
            )}
          </div>
        )}
      </div>

      {/* Add row */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-4">
        <div className="bg-white border border-stone-200 rounded-lg p-3 flex flex-wrap gap-2 items-center">
          <Plus size={14} className="text-stone-400 ml-1" />
          <input value={newRow.handle} onChange={(e) => setNewRow({ ...newRow, handle: e.target.value })} placeholder="@handle or instagram.com/handle"
            className="flex-[2] min-w-[180px] px-2.5 py-1.5 border border-stone-300 rounded text-sm" data-testid="outreach-add-handle" />
          <input value={newRow.property_name} onChange={(e) => setNewRow({ ...newRow, property_name: e.target.value })} placeholder="Property (optional)"
            className="flex-[2] min-w-[140px] px-2.5 py-1.5 border border-stone-300 rounded text-sm" />
          <input value={newRow.location} onChange={(e) => setNewRow({ ...newRow, location: e.target.value })} placeholder="Location (optional)"
            className="flex-1 min-w-[120px] px-2.5 py-1.5 border border-stone-300 rounded text-sm" />
          <button onClick={addNew} disabled={!newRow.handle.trim() || addingNew} className="px-4 py-1.5 bg-stone-900 text-white text-xs rounded hover:bg-stone-800 disabled:opacity-50" data-testid="outreach-add-submit">
            {addingNew ? '…' : 'Add'}
          </button>
        </div>
      </div>

      {/* Targets list */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-5">
        <p className="text-[11px] text-stone-500 mb-3" data-testid="outreach-count">Showing {filtered.length} of {total}</p>
        {loading && <p className="text-sm text-stone-500">Loading…</p>}
        {error && <p className="text-sm text-rose-700 mb-3">{error}</p>}

        {filtered.length === 0 && !loading && (
          <div className="bg-white border border-dashed border-stone-300 rounded-lg p-10 text-center">
            <Sparkles size={28} className="text-stone-300 mx-auto mb-3" />
            <p className="text-sm text-stone-600">No targets yet. Upload your Instagram following file or paste some handles to start.</p>
          </div>
        )}

        <ul className="space-y-1.5" data-testid="outreach-list">
          {filtered.map((t) => {
            const sm = STATUS_MAP[t.status] || STATUS_MAP.target_unscreened;
            const isDmOpen = activeDmHandle === t.handle;
            const profileUrl = `https://instagram.com/${t.handle}`;
            const waUrl = t.whatsapp ? `https://wa.me/${(t.whatsapp || '').replace(/\D/g, '')}` : null;

            return (
              <li key={t.handle} className={`bg-white border ${t.status === 'onboarded' ? 'border-emerald-300' : 'border-stone-200'} rounded-lg`} data-testid={`outreach-row-${t.handle}`}>
                <div className="flex items-center gap-3 px-3 py-2.5 flex-wrap">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${sm.c} whitespace-nowrap`}>{sm.l}</span>
                  <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-stone-900 font-mono hover:underline" data-testid={`outreach-handle-${t.handle}`}>@{t.handle}</a>
                  {t.property_name && <span className="text-xs text-stone-600 italic">· {t.property_name}</span>}
                  {t.location && <span className="text-[11px] text-stone-500">· {t.location}</span>}
                  {(() => {
                    const SOURCE_MAP = {
                      apply_form:          { label: 'inbound · /apply',  cls: 'bg-sky-100 text-sky-800',     title: 'Submitted directly via /apply' },
                      apply_form_backfill: { label: 'inbound · /apply',  cls: 'bg-sky-100 text-sky-800',     title: 'Backfilled from /apply (pre-link)' },
                      manual:              { label: 'manual',            cls: 'bg-amber-100 text-amber-800', title: 'Added manually from /admin/outreach' },
                      paste:               { label: 'bulk · paste',      cls: 'bg-stone-200 text-stone-700', title: 'Bulk-imported via paste' },
                      docx:                { label: 'bulk · docx',       cls: 'bg-stone-200 text-stone-700', title: 'Imported from Instagram .docx export' },
                      html:                { label: 'bulk · html',       cls: 'bg-stone-200 text-stone-700', title: 'Imported from Instagram HTML export' },
                      json:                { label: 'bulk · json',       cls: 'bg-stone-200 text-stone-700', title: 'Imported from Instagram JSON export' },
                    };
                    const sr = SOURCE_MAP[t.import_source];
                    if (!sr) return null;
                    return <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded whitespace-nowrap ${sr.cls}`} title={sr.title} data-testid={`source-${t.handle}`}>{sr.label}</span>;
                  })()}
                  {t.auto_linked_apply_id && <span className="text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded" title="Auto-linked from /apply">auto-linked</span>}

                  <div className="ml-auto flex items-center gap-1">
                    {/* Quick shortlist actions when unscreened */}
                    {t.status === 'target_unscreened' && (
                      <>
                        <IconBtn icon={Star} title="Shortlist" onClick={() => setStatus(t.handle, 'target')} testid={`shortlist-${t.handle}`} cls="text-amber-700 hover:bg-amber-100" />
                        <IconBtn icon={ThumbsDown} title="Not a fit" onClick={() => setStatus(t.handle, 'not_a_fit')} testid={`nofit-${t.handle}`} cls="text-stone-500 hover:bg-stone-100" />
                      </>
                    )}
                    {/* Compose DM */}
                    {['target', 'dm_sent', 'replied', 'in_conversation'].includes(t.status) && (
                      <IconBtn icon={MessageCircle} title="DM templates" onClick={() => setActiveDmHandle(isDmOpen ? null : t.handle)} testid={`dm-${t.handle}`} cls="text-sky-700 hover:bg-sky-100" active={isDmOpen} />
                    )}
                    {/* Open profile */}
                    <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded text-stone-600 hover:bg-stone-100" title="Open IG profile" data-testid={`open-${t.handle}`}>
                      <ExternalLink size={13} />
                    </a>
                    {/* WhatsApp if captured */}
                    {waUrl && (
                      <a href={waUrl} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded text-emerald-700 hover:bg-emerald-100" title="WhatsApp host" data-testid={`wa-${t.handle}`}>
                        <Send size={13} />
                      </a>
                    )}
                    {/* Open in /admin/review (only if linked to a /apply submission) */}
                    {t.auto_linked_apply_id && (
                      <a
                        href={`/admin/review?gh_id=${encodeURIComponent(t.auto_linked_apply_id)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded text-stone-600 hover:bg-amber-100 hover:text-amber-800"
                        title={`Open ${t.auto_linked_apply_id} in /admin/review`}
                        data-testid={`open-review-${t.handle}`}
                      ><FileText size={13} /></a>
                    )}
                    {/* Status changer */}
                    <select value={t.status} onChange={(e) => setStatus(t.handle, e.target.value)} className="text-[10px] px-1.5 py-1 border border-stone-300 rounded bg-white" data-testid={`status-${t.handle}`}>
                      {Object.entries(STATUS_MAP).sort((a, b) => a[1].order - b[1].order).map(([k, v]) => (
                        <option key={k} value={k}>{v.l}</option>
                      ))}
                    </select>
                    <IconBtn icon={Pencil} title="Edit handle, property, location, WhatsApp, notes" onClick={() => (editingHandle === t.handle ? cancelEdit() : startEdit(t))} testid={`edit-${t.handle}`} cls="text-amber-700 hover:bg-amber-100" active={editingHandle === t.handle} />
                    <IconBtn icon={Trash2} title="Delete" onClick={() => deleteTarget(t.handle)} testid={`del-${t.handle}`} cls="text-rose-600 hover:bg-rose-100" />
                  </div>
                </div>

                {/* Inline edit drawer */}
                {editingHandle === t.handle && (
                  <div className="border-t border-stone-200 px-4 py-4 bg-amber-50/50" data-testid={`edit-drawer-${t.handle}`}>
                    <div className="grid md:grid-cols-5 gap-2.5 mb-2">
                      <label className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-wider text-stone-600">Instagram handle</span>
                        <input
                          value={editForm.handle}
                          onChange={(e) => setEditForm({ ...editForm, handle: e.target.value })}
                          placeholder="realhandle (no @)"
                          className="px-2 py-1.5 text-xs border border-stone-300 rounded font-mono focus:outline-none focus:border-stone-900"
                          data-testid={`edit-handle-${t.handle}`}
                        />
                      </label>
                      <label className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-wider text-stone-600">Property name</span>
                        <input
                          value={editForm.property_name}
                          onChange={(e) => setEditForm({ ...editForm, property_name: e.target.value })}
                          className="px-2 py-1.5 text-xs border border-stone-300 rounded focus:outline-none focus:border-stone-900"
                          data-testid={`edit-name-${t.handle}`}
                        />
                      </label>
                      <label className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-wider text-stone-600">Location</span>
                        <input
                          value={editForm.location}
                          onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                          className="px-2 py-1.5 text-xs border border-stone-300 rounded focus:outline-none focus:border-stone-900"
                          data-testid={`edit-loc-${t.handle}`}
                        />
                      </label>
                      <label className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-wider text-stone-600">WhatsApp</span>
                        <input
                          value={editForm.whatsapp}
                          onChange={(e) => setEditForm({ ...editForm, whatsapp: e.target.value })}
                          placeholder="+91 / +977 …"
                          className="px-2 py-1.5 text-xs border border-stone-300 rounded font-mono focus:outline-none focus:border-stone-900"
                          data-testid={`edit-wa-${t.handle}`}
                        />
                      </label>
                      <label className="flex flex-col gap-1 md:col-span-1">
                        <span className="text-[10px] uppercase tracking-wider text-stone-600">Notes</span>
                        <input
                          value={editForm.notes}
                          onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                          className="px-2 py-1.5 text-xs border border-stone-300 rounded focus:outline-none focus:border-stone-900"
                          data-testid={`edit-notes-${t.handle}`}
                        />
                      </label>
                    </div>
                    {editError && <p className="text-[11px] text-rose-700 mb-2" data-testid={`edit-error-${t.handle}`}>{editError}</p>}
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={cancelEdit}
                        disabled={editSaving}
                        className="text-[11px] px-3 py-1.5 rounded border border-stone-300 text-stone-700 hover:bg-stone-100 inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`edit-cancel-${t.handle}`}
                      ><X size={11} /> Cancel</button>
                      <button
                        onClick={() => saveEdit(t.handle, t.status)}
                        disabled={editSaving}
                        className="text-[11px] px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`edit-save-${t.handle}`}
                      >{editSaving ? '…' : <><Save size={11} /> Save changes</>}</button>
                    </div>
                  </div>
                )}

                {/* DM templates panel */}
                {isDmOpen && (
                  <div className="border-t border-stone-200 px-4 py-4 bg-sky-50/40">
                    <p className="text-[10px] uppercase tracking-widest text-sky-900/70 mb-3 font-semibold">DM templates · personalise before sending</p>
                    <div className="grid md:grid-cols-3 gap-3">
                      {DM_TEMPLATES.map((tpl) => {
                        const text = tpl.body(t.handle);
                        const key = `dm-${t.handle}-${tpl.k}`;
                        return (
                          <div key={tpl.k} className="bg-white border border-stone-200 rounded p-3">
                            <div className="flex items-center justify-between mb-1.5">
                              <p className="text-[11px] font-semibold text-stone-700">{tpl.label}</p>
                              <button onClick={() => copy(text, key)} className="text-[10px] inline-flex items-center gap-1 px-2 py-1 bg-stone-900 text-white rounded hover:bg-stone-800">
                                {copiedKey === key ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
                              </button>
                            </div>
                            <pre className="text-[10.5px] text-stone-700 whitespace-pre-wrap leading-snug font-sans max-h-32 overflow-y-auto">{text}</pre>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-end gap-2 mt-3">
                      <button onClick={() => setStatus(t.handle, 'dm_sent')} className="text-[11px] px-3 py-1.5 bg-sky-600 text-white rounded hover:bg-sky-700 inline-flex items-center gap-1" data-testid={`mark-sent-${t.handle}`}>
                        <ChevronRight size={12} /> Mark DM sent
                      </button>
                      <button onClick={() => setStatus(t.handle, 'replied')} className="text-[11px] px-3 py-1.5 bg-violet-600 text-white rounded hover:bg-violet-700 inline-flex items-center gap-1" data-testid={`mark-replied-${t.handle}`}>
                        <ChevronRight size={12} /> Mark replied
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

const Stat = ({ k, v, highlight }) => (
  <div className={`px-2.5 py-1.5 rounded ${highlight ? 'bg-amber-300/20 border border-amber-300/40' : 'bg-stone-800 border border-stone-700'}`}>
    <div className="text-sm font-semibold text-amber-100 leading-none">{v}</div>
    <div className="text-[9px] uppercase tracking-widest text-stone-400 mt-0.5">{k}</div>
  </div>
);

const IconBtn = ({ icon: Icon, title, onClick, testid, cls, active }) => (
  <button onClick={onClick} title={title} data-testid={testid} className={`p-1.5 rounded transition ${cls || ''} ${active ? 'bg-stone-200' : ''}`}>
    <Icon size={13} />
  </button>
);
